import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle2, Users, Briefcase, Monitor, TrendingUp, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import PublicNavbar from '@/components/landing/PublicNavbar';
import PublicFooter from '@/components/landing/PublicFooter';
import { base44 } from '@/api/base44Client';
import { useI18n } from '@/lib/I18nContext';

function useIntersection(ref, threshold = 0.15) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

function useCountUp(end, suffix, duration = 2000, start = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setVal(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start]);
  return val.toLocaleString('es-CL') + suffix;
}

const PROFILES = [
  {
    icon: Briefcase,
    color: '#00D5FF',
    bg: 'rgba(0,213,255,0.1)',
    titleKey: 'use_cases.agencies_title',
    subKey: 'use_cases.agencies_sub',
    descKey: 'use_cases.agencies_desc',
    benefitKeys: ['use_cases.agencies_benefit_1', 'use_cases.agencies_benefit_2', 'use_cases.agencies_benefit_3', 'use_cases.agencies_benefit_4'],
  },
  {
    icon: Users,
    color: '#7C3AED',
    bg: 'rgba(124,58,237,0.1)',
    titleKey: 'use_cases.freelancers_title',
    subKey: 'use_cases.freelancers_sub',
    descKey: 'use_cases.freelancers_desc',
    benefitKeys: ['use_cases.freelancers_benefit_1', 'use_cases.freelancers_benefit_2', 'use_cases.freelancers_benefit_3', 'use_cases.freelancers_benefit_4'],
  },
  {
    icon: Monitor,
    color: '#22C55E',
    bg: 'rgba(34,197,94,0.1)',
    titleKey: 'use_cases.community_title',
    subKey: 'use_cases.community_sub',
    descKey: 'use_cases.community_desc',
    benefitKeys: ['use_cases.community_benefit_1', 'use_cases.community_benefit_2', 'use_cases.community_benefit_3', 'use_cases.community_benefit_4'],
  },
  {
    icon: TrendingUp,
    color: '#F59F00',
    bg: 'rgba(245,159,0,0.1)',
    titleKey: 'use_cases.brands_title',
    subKey: 'use_cases.brands_sub',
    descKey: 'use_cases.brands_desc',
    benefitKeys: ['use_cases.brands_benefit_1', 'use_cases.brands_benefit_2', 'use_cases.brands_benefit_3', 'use_cases.brands_benefit_4'],
  },
];

const TESTIMONIALS = [
  {
    name: 'Valentina Rojas',
    roleKey: 'use_cases.testimonial_1_role',
    agency: 'Agencia Norte Digital',
    avatar: 'VR',
    avatarColor: 'linear-gradient(135deg, #00D5FF, #7C3AED)',
    textKey: 'use_cases.testimonial_1_text',
  },
  {
    name: 'Carlos Méndez',
    roleKey: 'use_cases.testimonial_2_role',
    agency: 'CM Studio',
    avatar: 'CM',
    avatarColor: 'linear-gradient(135deg, #7C3AED, #00D5FF)',
    textKey: 'use_cases.testimonial_2_text',
  },
  {
    name: 'Daniela Torres',
    roleKey: 'use_cases.testimonial_3_role',
    agency: 'Maqsale',
    avatar: 'DT',
    avatarColor: 'linear-gradient(135deg, #22C55E, #00D5FF)',
    textKey: 'use_cases.testimonial_3_text',
  },
];

const STATS_KEYS = [
  { end: 500, suffix: '+', labelKey: 'use_cases.stat_reports' },
  { end: 10000, suffix: '+', labelKey: 'use_cases.stat_campaigns' },
  { end: 3, suffix: 'h', labelKey: 'use_cases.stat_hours' },
];

function StatBlock({ end, suffix, labelKey, start, t }) {
  const val = useCountUp(end, suffix, 2000, start);
  return (
    <div className="text-center">
      <p className="font-heading font-black text-5xl mb-2" style={{ color: '#00D5FF' }}>{val}</p>
      <p className="text-sm" style={{ color: '#94A3B8' }}>{t(labelKey)}</p>
    </div>
  );
}

export default function CasosDeUso() {
  const { t } = useI18n();
  const [testimonialIdx, setTestimonialIdx] = useState(0);
  const statsRef = useRef(null);
  const statsVisible = useIntersection(statsRef, 0.3);
  const cardsRef = useRef(null);
  const cardsVisible = useIntersection(cardsRef);

  useEffect(() => {
    const interval = setInterval(() => setTestimonialIdx(i => (i + 1) % TESTIMONIALS.length), 4000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen font-body overflow-x-hidden" style={{ background: '#070B14', color: '#F8FAFC' }}>
      <PublicNavbar />

      {/* Hero */}
      <section
        className="relative pt-32 pb-20 px-6 text-center overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #070B14 0%, #0d1525 50%, #111827 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 50% 60%, rgba(124,58,237,0.15) 0%, transparent 60%)' }} />
        <div className="relative z-10 max-w-3xl mx-auto">
          <h1 className="font-heading font-bold text-white mb-4" style={{ fontSize: 'clamp(1.8rem, 4.5vw, 2.8rem)', lineHeight: 1.2 }}>
            {t('use_cases.title')}
          </h1>
          <p className="text-lg leading-relaxed" style={{ color: '#94A3B8' }}>
            {t('use_cases.subtitle')}
          </p>
        </div>
      </section>

      {/* Profile cards */}
      <section className="py-20 px-6" style={{ background: '#0d1525' }}>
        <div className="max-w-5xl mx-auto">
          <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {PROFILES.map((p, i) => (
              <div
                key={p.titleKey}
                className="rounded-3xl p-7 hover:shadow-2xl transition-shadow"
                style={{
                  background: '#111827',
                  border: '1px solid #1E2D45',
                  transition: `opacity 0.6s ease ${i * 100}ms, transform 0.6s ease ${i * 100}ms`,
                  opacity: cardsVisible ? 1 : 0,
                  transform: cardsVisible ? 'translateY(0)' : 'translateY(24px)',
                }}
              >
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: p.bg }}>
                    <p.icon className="w-6 h-6" style={{ color: p.color }} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-xl" style={{ color: '#F8FAFC' }}>{t(p.titleKey)}</h3>
                    <p className="text-sm font-medium mt-0.5" style={{ color: p.color }}>{t(p.subKey)}</p>
                  </div>
                </div>
                <p className="text-sm leading-relaxed mb-5" style={{ color: '#94A3B8' }}>{t(p.descKey)}</p>
                <ul className="space-y-2.5 mb-6">
                  {p.benefitKeys.map(bk => (
                    <li key={bk} className="flex items-start gap-2.5 text-sm" style={{ color: '#94A3B8' }}>
                      <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" style={{ color: p.color }} />
                      {t(bk)}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => base44.auth.redirectToLogin()}
                  className="w-full flex items-center justify-center gap-2 font-semibold text-sm py-3 rounded-xl transition-all hover:opacity-90"
                  style={{ background: p.color, color: '#070B14' }}
                >
                  {t('landing.start_free')} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section ref={statsRef} className="py-20 px-6" style={{ background: 'linear-gradient(135deg, #070B14, #111827, #0d1525)' }}>
        <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-10">
          {STATS_KEYS.map(s => (
            <StatBlock key={s.labelKey} end={s.end} suffix={s.suffix} labelKey={s.labelKey} start={statsVisible} t={t} />
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6" style={{ background: '#0d1525' }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading font-bold text-3xl text-center mb-12" style={{ color: '#F8FAFC' }}>{t('use_cases.testimonials_title')}</h2>
          <div className="relative">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${testimonialIdx * 100}%)` }}
              >
                {TESTIMONIALS.map((item, i) => (
                  <div key={i} className="w-full shrink-0 px-2">
                    <div className="rounded-2xl p-8 text-center" style={{ background: '#111827', border: '1px solid #1E2D45' }}>
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 font-heading font-bold text-lg"
                        style={{ background: item.avatarColor, color: '#F8FAFC' }}
                      >
                        {item.avatar}
                      </div>
                      <p className="text-base leading-relaxed mb-5 italic" style={{ color: '#94A3B8' }}>"{t(item.textKey)}"</p>
                      <p className="font-heading font-bold" style={{ color: '#F8FAFC' }}>{item.name}</p>
                      <p className="text-sm" style={{ color: '#475569' }}>{t(item.roleKey)} · {item.agency}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-center gap-3 mt-6">
              <button onClick={() => setTestimonialIdx(i => (i - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)} className="p-2 rounded-full border transition-colors" style={{ borderColor: '#1E2D45' }}>
                <ChevronLeft className="w-4 h-4" style={{ color: '#94A3B8' }} />
              </button>
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setTestimonialIdx(i)}
                  className="w-2 h-2 rounded-full transition-all"
                  style={{ background: i === testimonialIdx ? '#00D5FF' : '#1E2D45' }}
                />
              ))}
              <button onClick={() => setTestimonialIdx(i => (i + 1) % TESTIMONIALS.length)} className="p-2 rounded-full border transition-colors" style={{ borderColor: '#1E2D45' }}>
                <ChevronRight className="w-4 h-4" style={{ color: '#94A3B8' }} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6" style={{ background: 'linear-gradient(135deg, #070B14, #111827)' }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-heading font-bold text-white text-4xl mb-4 leading-tight">{t('how_it_works_page.cta')}</h2>
          <p className="text-lg mb-8" style={{ color: '#94A3B8' }}>{t('landing.pricing_free_trial')}, {t('landing.pricing_no_card').toLowerCase()}, {t('landing.pricing_cancel').toLowerCase()}</p>
          <button
            onClick={() => base44.auth.redirectToLogin()}
            className="inline-flex items-center gap-2 font-bold text-lg px-10 py-4 rounded-2xl transition-all hover:opacity-90 hover:scale-105 shadow-xl"
            style={{ background: '#00D5FF', color: '#070B14' }}
          >
            {t('landing.start_free')} <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}